/**
 * Scanner XML minimal, orienté flux.
 *
 * L'application Android utilise un pull parser : elle ne matérialise jamais
 * l'arbre du document. On reproduit ce choix plutôt que d'utiliser
 * `DOMParser`, pour trois raisons :
 *
 *  - mémoire — un GPX de 200 000 points produirait un DOM de plusieurs
 *    centaines de mégaoctets, rédhibitoire sur mobile ;
 *  - sécurité — aucune déclaration d'entité n'est jamais développée, ce qui
 *    ferme la porte aux attaques par expansion d'entités ;
 *  - fidélité — le parseur Android compare les noms de balises bruts,
 *    préfixe compris (`velogpx:marker`). Un parseur sensible aux espaces de
 *    noms ne se comporterait pas de la même manière sur les fichiers réels,
 *    où le préfixe varie.
 *
 * Ce scanner ne vise pas la conformité XML complète : il couvre ce qu'on
 * rencontre dans un fichier GPX, et ignore silencieusement le reste.
 */

export interface XmlStartTag {
  readonly kind: 'start'
  /** Nom brut, préfixe inclus. */
  readonly name: string
  readonly attributes: ReadonlyMap<string, string>
  readonly selfClosing: boolean
}

export interface XmlEndTag {
  readonly kind: 'end'
  readonly name: string
}

export interface XmlText {
  readonly kind: 'text'
  readonly text: string
}

export type XmlEvent = XmlStartTag | XmlEndTag | XmlText

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
}

/**
 * Remplace les références de caractères et les cinq entités prédéfinies.
 *
 * Toute autre entité est laissée telle quelle : sans table de déclarations,
 * on ne peut pas la résoudre, et l'inventer serait pire que de la conserver.
 */
export function decodeXmlText(input: string): string {
  if (!input.includes('&')) {
    return input
  }

  return input.replace(
    /&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z][a-zA-Z0-9]*);/g,
    (match, entity: string) => {
      if (entity.startsWith('#x') || entity.startsWith('#X')) {
        const code = Number.parseInt(entity.slice(2), 16)
        return Number.isFinite(code) ? safeFromCodePoint(code, match) : match
      }
      if (entity.startsWith('#')) {
        const code = Number.parseInt(entity.slice(1), 10)
        return Number.isFinite(code) ? safeFromCodePoint(code, match) : match
      }
      return NAMED_ENTITIES[entity] ?? match
    },
  )
}

function safeFromCodePoint(code: number, fallback: string): string {
  if (code < 0 || code > 0x10ffff) return fallback
  try {
    return String.fromCodePoint(code)
  } catch {
    return fallback
  }
}

function isNameChar(char: string): boolean {
  return !(
    char === ' ' ||
    char === '\t' ||
    char === '\n' ||
    char === '\r' ||
    char === '>' ||
    char === '/'
  )
}

/**
 * Parcourt le document et émet un événement par balise et par bloc de texte.
 *
 * Les instructions de traitement, commentaires et déclarations de type de
 * document sont sautés sans être interprétés.
 */
export function* scanXml(source: string): Generator<XmlEvent, void, undefined> {
  const length = source.length
  let index = 0

  while (index < length) {
    const lt = source.indexOf('<', index)

    if (lt === -1) {
      const tail = source.slice(index)
      if (tail.trim().length > 0) {
        yield { kind: 'text', text: decodeXmlText(tail) }
      }
      return
    }

    if (lt > index) {
      const text = source.slice(index, lt)
      if (text.trim().length > 0) {
        yield { kind: 'text', text: decodeXmlText(text) }
      }
    }

    // Commentaire
    if (source.startsWith('<!--', lt)) {
      const end = source.indexOf('-->', lt + 4)
      index = end === -1 ? length : end + 3
      continue
    }

    // Section littérale : le contenu est du texte, jamais du balisage.
    if (source.startsWith('<![CDATA[', lt)) {
      const end = source.indexOf(']]>', lt + 9)
      const text = source.slice(lt + 9, end === -1 ? length : end)
      if (text.length > 0) {
        yield { kind: 'text', text }
      }
      index = end === -1 ? length : end + 3
      continue
    }

    // Déclaration de type de document : sautée sans être lue, donc aucune
    // entité personnalisée ne peut être définie ni développée.
    if (source.startsWith('<!', lt)) {
      const end = skipDoctype(source, lt)
      index = end
      continue
    }

    // Instruction de traitement (`<?xml ... ?>`)
    if (source.startsWith('<?', lt)) {
      const end = source.indexOf('?>', lt + 2)
      index = end === -1 ? length : end + 2
      continue
    }

    // Balise fermante
    if (source.startsWith('</', lt)) {
      const end = source.indexOf('>', lt + 2)
      if (end === -1) return
      yield { kind: 'end', name: source.slice(lt + 2, end).trim() }
      index = end + 1
      continue
    }

    // Balise ouvrante
    const tagEnd = findTagEnd(source, lt + 1)
    if (tagEnd === -1) return

    const raw = source.slice(lt + 1, tagEnd)
    const selfClosing = raw.endsWith('/')
    const body = selfClosing ? raw.slice(0, -1) : raw

    let nameEnd = 0
    while (nameEnd < body.length && isNameChar(body[nameEnd]!)) {
      nameEnd++
    }

    const name = body.slice(0, nameEnd)
    const attributes = parseAttributes(body.slice(nameEnd))

    yield { kind: 'start', name, attributes, selfClosing }
    index = tagEnd + 1
  }
}

/** Fin d'une balise ouvrante, en ignorant les `>` situés dans les valeurs. */
function findTagEnd(source: string, from: number): number {
  let quote: string | null = null

  for (let i = from; i < source.length; i++) {
    const char = source[i]!

    if (quote) {
      if (char === quote) quote = null
    } else if (char === '"' || char === "'") {
      quote = char
    } else if (char === '>') {
      return i
    }
  }

  return -1
}

/** Fin d'une déclaration `<!DOCTYPE …>`, sous-ensemble interne compris. */
function skipDoctype(source: string, from: number): number {
  let depth = 0

  for (let i = from; i < source.length; i++) {
    const char = source[i]!
    if (char === '[') depth++
    else if (char === ']') depth--
    else if (char === '>' && depth <= 0) return i + 1
  }

  return source.length
}

const ATTRIBUTE_PATTERN = /([^\s=/]+)\s*=\s*("([^"]*)"|'([^']*)')/g

function parseAttributes(source: string): ReadonlyMap<string, string> {
  const attributes = new Map<string, string>()
  if (source.trim().length === 0) {
    return attributes
  }

  ATTRIBUTE_PATTERN.lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = ATTRIBUTE_PATTERN.exec(source)) !== null) {
    const name = match[1]!
    const value = match[3] ?? match[4] ?? ''
    attributes.set(name, decodeXmlText(value))
  }

  return attributes
}

/* -------------------------------------------------------------------------
 * Échappement — côté écriture
 * ---------------------------------------------------------------------- */

/**
 * Échappe le contenu textuel.
 *
 * Seuls `&`, `<` et `>` sont traités : les guillemets n'ont pas de sens
 * particulier hors d'une valeur d'attribut.
 */
export function escapeXmlText(value: string): string {
  if (!/[&<>]/.test(value)) {
    return value
  }
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** Échappe une valeur d'attribut, guillemets compris. */
export function escapeXmlAttribute(value: string): string {
  if (!/[&<>"']/.test(value)) {
    return value
  }
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}
